import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Check, ArrowRight } from "lucide-react";
import SharedNavbar from './SharedNavbar';

const pricingPlans = [
  {
    nameKey: "pricing.free.name",
    price: "0",
    periodKey: "pricing.free.period",
    descriptionKey: "pricing.free.description",
    featuresKey: "pricing.free.features",
    popular: false,
    ctaKey: "pricing.free.cta",
  },
  {
    nameKey: "pricing.premium.name",
    price: "4.99",
    periodKey: "pricing.premium.period",
    descriptionKey: "pricing.premium.description",
    featuresKey: "pricing.premium.features",
    popular: true,
    ctaKey: "pricing.premium.cta",
  },
  {
    nameKey: "pricing.pro.name",
    price: "9.99",
    periodKey: "pricing.pro.period",
    descriptionKey: "pricing.pro.description",
    featuresKey: "pricing.pro.features",
    popular: false,
    ctaKey: "pricing.pro.cta",
  },
];

const Pricing: React.FC = () => {
  const { t } = useTranslation();

  const handleGetStarted = () => {
    window.location.href = '/register';
  };

  return (
    <>
      <SharedNavbar />
      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                {t('pricing.title')}
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {t('pricing.subtitle')}
              </p>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {pricingPlans.map((plan, index) => (
                <Card
                  key={index}
                  className={`relative border-2 transition-all duration-300 hover:shadow-xl ${
                    plan.popular ? "border-emerald-500 shadow-lg scale-105" : "border-gray-200 hover:border-emerald-300"
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <Badge className="bg-emerald-600 text-white px-4 py-1">
                        {t('pricing.mostPopular')}
                      </Badge>
                    </div>
                  )}

                  <CardHeader className="text-center pb-8">
                    <CardTitle className="text-2xl font-bold text-gray-900">{t(plan.nameKey)}</CardTitle>
                    <div className="mt-4">
                      <span className="text-4xl font-bold text-gray-900">€{plan.price}</span>
                      <span className="text-gray-500">/{t(plan.periodKey)}</span>
                    </div>
                    <CardDescription className="mt-2">{t(plan.descriptionKey)}</CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4">
                    <ul className="space-y-3">
                      {(() => {
                        const features = t(plan.featuresKey, { returnObjects: true });
                        if (Array.isArray(features)) {
                          return features.map((feature: any, featureIndex: number) => (
                            <li key={featureIndex} className="flex items-center gap-3">
                              <Check className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                              <span className="text-gray-700">{String(feature)}</span>
                            </li>
                          ));
                        }
                        return null;
                      })()}
                    </ul>

                    <Button
                      className={`w-full mt-8 ${
                        plan.popular ? "bg-emerald-600 hover:bg-emerald-700" : "bg-gray-900 hover:bg-gray-800"
                      }`}
                      size="lg"
                      onClick={handleGetStarted}
                    >
                      {t(plan.ctaKey)}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Pricing; 